import { FC, useEffect, useState } from 'react'
import { useSearchParams } from '@/hooks/useSearchParams'
import usePagination from '@/hooks/usePagination'
import Pagination from '@UI/New/Pagination'
import { reportAPI } from '@/api'
import CustomTable from '@common/CustomTable/Table'
import lessonTableStyles from '@pages/Admin/Reports/components/lessonTable.module.css'
import { IPrivateAllLessonItem } from '../../utils/types'
import usePrivateAllLessonsColumns from '../TableColumns/usePrivateAllLessonsColumns'
import cn from '../PrivateLessonReportTable/index.module.css'
import ChangeLessonReportStatusModal from '../../../GroupReport/components/MakeLessonReportModal/ChangeLessonReportStatusModal'
import EditEntityModal from '@modals/EditEntityModal/EditEntityModal'
import useRoles from '@/hooks/useRoles'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import LessonStatusCards from '@pages/Admin/AllUsers/modals/EditStudentModal/components/LessonStatusesTab/LessonStatusCards'

interface IPrivateAllLessonsTable {
  refreshKey?: string
  studentId?: number | string | null
  directionId?: string | null
  startDate?: string | null
  endDate?: string | null
  status?: string | null
  onReportChange?: () => void
  viewMode?: 'table' | 'cards'
  paginationContainer?: HTMLElement | null
}

const PrivateAllLessonsTable: FC<IPrivateAllLessonsTable> = ({
  refreshKey,
  studentId,
  directionId: selectedDirectionId,
  startDate,
  endDate,
  status,
  onReportChange,
  viewMode = 'table',
  paginationContainer,
}) => {
  const { isAdminOrJuniorAdmin } = useRoles()
  const canEdit = useSelector((store: any) => {
    const role = store.profile?.profile?.role
    const isTeacher = ['seminarian', 'senior-seminarian'].includes(role)

    return !isTeacher || store.settings?.isTeacherCanEditStatuses === '1'
  })
  const [startDateFromSearch] = useSearchParams('all-start-date')
  const [endDateFromSearch] = useSearchParams('all-end-date')
  const [directionId] = useSearchParams('direction-id')
  const [seminarianId] = useSearchParams('all-seminarian-id')
  const [studentIdFromSearch] = useSearchParams('all-student-id')
  const [statusFromSearch] = useSearchParams('all-status')
  const [search] = useSearchParams('all-search')
  const [franchiseIds] = useSearchParams('franchise-ids')
  const [editReport, setEditReport] = useState<IPrivateAllLessonItem | null>(
    null,
  )
  const [userModal, setUserModal] = useState<{
    user: IUser
    role: 'student' | 'employee'
  } | null>(null)
  const [sorting, setSorting] = useState(
    [
      'date-time',
      'seminarian',
      'student',
      'course',
      'duration',
      'status',
      'price',
      'salary',
      'comment',
    ].map((key) => ({
      key,
      order: 'none' as 'asc' | 'desc' | 'none',
    })),
  )
  const currentSort = viewMode === 'cards'
    ? { key: 'date-time', order: 'asc' }
    : sorting.find((item) => item.order !== 'none')
  const effectiveStartDate = startDate ?? startDateFromSearch
  const effectiveEndDate = endDate ?? endDateFromSearch
  const effectiveStudentId = studentId ?? studentIdFromSearch
  const effectiveStatus = status ?? statusFromSearch
  const effectiveDirectionId =
    selectedDirectionId === undefined ? directionId : selectedDirectionId

  const { state, loading, getState, ...params } = usePagination({
    api: reportAPI.private.allLessons,
    params: {
      start_date: effectiveStartDate,
      end_date: effectiveEndDate,
      ...(effectiveDirectionId ? { direction_id: effectiveDirectionId } : {}),
      ...(!studentId && seminarianId ? { seminarian_id: seminarianId } : {}),
      ...(effectiveStudentId ? { student_id: effectiveStudentId } : {}),
      ...(effectiveStatus && effectiveStatus !== 'all'
        ? { status: effectiveStatus }
        : {}),
      ...(!studentId && search ? { search } : {}),
      ...(!studentId && franchiseIds ? { franchise_ids: franchiseIds } : {}),
      ...(currentSort
        ? { sort_key: currentSort.key, sort_order: currentSort.order }
        : {}),
    },
    firstRequest: false,
    latestRequestOnly: true,
    initialPerPage: 100,
  })

  useEffect(() => {
    getState({ page: 1 })
    params.setCurrentPage(1)
  }, [
    effectiveStartDate,
    effectiveEndDate,
    effectiveDirectionId,
    seminarianId,
    effectiveStudentId,
    effectiveStatus,
    search,
    franchiseIds,
    currentSort?.key,
    currentSort?.order,
    refreshKey,
  ])

  const columns = usePrivateAllLessonsColumns({
    onEdit: (report) => setEditReport(report),
    canOpenSeminarianCards: isAdminOrJuniorAdmin,
    onStudentClick: (user) => {
      if (!user?.id) return
      setUserModal({ user, role: 'student' })
    },
    onSeminarianClick: (user) => {
      if (!isAdminOrJuniorAdmin) return
      if (!user?.id) return
      setUserModal({ user, role: 'employee' })
    },
  })
  const visibleColumns = studentId
    ? columns.filter((column) => column.id !== 'student')
    : columns
  const data = state as IPrivateAllLessonItem[]
  const pagination = <Pagination compact={!!paginationContainer} {...params} />

  return (
    <div className={cn.content}>
      {viewMode === 'cards' ? (
        <LessonStatusCards
          loading={loading}
          items={data.map((lesson) => ({
            id: lesson.id,
            dateTime: lesson.date_time,
            dateConference: lesson.date_conference,
            startTime: lesson.start_time,
            status: lesson.status,
            statusText: lesson.status_text,
            teacher: lesson.seminarian?.name,
            course: lesson.course_name,
            duration: lesson.duration,
            income: lesson.price,
            salary: lesson.salary,
            comment: lesson.comment,
            isPaid: lesson.is_paid,
            hasEnoughBalance: lesson.has_enough_balance,
            onEdit: canEdit ? () => setEditReport(lesson) : undefined,
          }))}
        />
      ) : (
        <CustomTable<IPrivateAllLessonItem>
          className={lessonTableStyles.table}
          flexColumns
          compact
          dense={Boolean(studentId)}
          dragScroll={Boolean(studentId)}
          data={data}
          columns={visibleColumns}
          loading={loading}
          sorting={sorting}
          onSortingClick={(key) => {
            setSorting((prev) =>
              prev.map((item) => ({
                ...item,
                order:
                  item.key === key
                    ? item.order === 'none'
                      ? 'asc'
                      : item.order === 'asc'
                      ? 'desc'
                      : 'none'
                    : 'none',
              })),
            )
          }}
        />
      )}
      {paginationContainer
        ? createPortal(pagination, paginationContainer)
        : pagination}
      <ChangeLessonReportStatusModal
        open={!!editReport}
        report={
          editReport
            ? ({ ...editReport, conference_type: 'private' } as any)
            : null
        }
        onSuccess={() => {
          getState()
          onReportChange?.()
          setEditReport(null)
        }}
        onDismiss={() => setEditReport(null)}
      />
      {userModal?.role === 'employee' && (
        <EditEntityModal
          type="employee"
          open={!!userModal}
          employeeId={userModal?.user?.id ?? null}
          onDismiss={() => setUserModal(null)}
          onUpdate={() => setUserModal(null)}
        />
      )}
      {userModal?.role === 'student' && (
        <EditEntityModal
          type="student"
          open={!!userModal}
          studentId={userModal?.user?.id ?? null}
          onDismiss={() => setUserModal(null)}
          onUpdate={() => setUserModal(null)}
        />
      )}
    </div>
  )
}

export default PrivateAllLessonsTable
